import { useState, useRef, useCallback, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import api from '@/lib/api'
import {
  Upload,
  Search,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  X,
  Plus,
  Loader2,
} from 'lucide-react'

const PERFORMANCE_TYPES = [
  { id: 1, label: 'LIVE' },
  { id: 2, label: 'BACKTEST' },
  { id: 3, label: 'ESTIMATED' },
]

const VALUE_TYPES = [
  { id: 'net_performance', label: 'Net Performance' },
  { id: 'gross_performance', label: 'Gross Performance' },
  { id: 'nav', label: 'NAV (will compute returns)' },
]

type Step = 'fund' | 'file' | 'sheet' | 'mapping' | 'confirm' | 'result'

interface FundResult {
  fund_id: number
  name: string
  firm_name?: string
}

interface ParseResult {
  filename: string
  sheets: string[]
  selected_sheet: string | null
  columns: string[]
  row_count: number
  sample_rows: string[][]
}

interface DateDetection {
  format: string | null
  confidence: number
  parsed_samples: (string | null)[]
}

interface ImportResult {
  inserted: number
  updated: number
  skipped: number
  errors: string[]
  date_range?: { min: string; max: string }
}

export default function FundDataUpload() {
  const [step, setStep] = useState<Step>('fund')

  // Step 1: Fund selection
  const [fundQuery, setFundQuery] = useState('')
  const [fundResults, setFundResults] = useState<FundResult[]>([])
  const [selectedFund, setSelectedFund] = useState<FundResult | null>(null)
  const [fundSearching, setFundSearching] = useState(false)
  const [showCreateFund, setShowCreateFund] = useState(false)
  const [newFundName, setNewFundName] = useState('')
  const [newManagerQuery, setNewManagerQuery] = useState('')
  const [managerResults, setManagerResults] = useState<{ manager_id: number; name: string }[]>([])
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null)
  const [newStrategy, setNewStrategy] = useState('')
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>()

  // Step 2: File upload
  const [file, setFile] = useState<File | null>(null)
  const [parseResult, setParseResult] = useState<ParseResult | null>(null)

  // Step 3: Sheet selection (Excel only)
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null)

  // Step 4: Column mapping
  const [dateCol, setDateCol] = useState<string>('')
  const [valueCol, setValueCol] = useState<string>('')
  const [valueType, setValueType] = useState<string>('net_performance')
  const [dateDetection, setDateDetection] = useState<DateDetection | null>(null)
  const [dateFormat, setDateFormat] = useState<string | null>(null)

  // Step 5: Confirm
  const [performanceTypeId, setPerformanceTypeId] = useState(1)
  const [onConflict, setOnConflict] = useState<'skip' | 'overwrite'>('skip')

  // Step 6: Result
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  // Message
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // --- Fund search with debounce ---
  const searchFunds = useCallback((q: string) => {
    if (q.length < 2) {
      setFundResults([])
      return
    }
    setFundSearching(true)
    api.get(`/fund-upload/search-funds?q=${encodeURIComponent(q)}`)
      .then((r) => setFundResults(r.data))
      .catch(() => setFundResults([]))
      .finally(() => setFundSearching(false))
  }, [])

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => searchFunds(fundQuery), 300)
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current) }
  }, [fundQuery, searchFunds])

  // --- Manager search ---
  const searchManagers = useCallback((q: string) => {
    if (q.length < 2) {
      setManagerResults([])
      return
    }
    api.get(`/fund-upload/search-managers?q=${encodeURIComponent(q)}`)
      .then((r) => setManagerResults(r.data))
      .catch(() => setManagerResults([]))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => searchManagers(newManagerQuery), 300)
    return () => clearTimeout(t)
  }, [newManagerQuery, searchManagers])

  // --- Create fund mutation ---
  const createFundMutation = useMutation({
    mutationFn: (payload: { name: string; manager_id: number | null; strategy: string | null }) =>
      api.post('/fund-upload/create-fund', payload).then((r) => r.data),
    onSuccess: (data) => {
      setSelectedFund({ fund_id: data.fund_id, name: data.name })
      setShowCreateFund(false)
      setMessage({ type: 'success', text: `Fund "${data.name}" created (ID: ${data.fund_id})` })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create fund' })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  // --- Create manager mutation ---
  const createManagerMutation = useMutation({
    mutationFn: (name: string) =>
      api.post('/fund-upload/create-manager', { name }).then((r) => r.data),
    onSuccess: (data) => {
      setSelectedManagerId(data.manager_id)
      setNewManagerQuery(data.name)
      setManagerResults([])
      setMessage({ type: 'success', text: `Manager "${data.name}" created` })
      setTimeout(() => setMessage(null), 3000)
    },
  })

  // --- File parse mutation ---
  const parseMutation = useMutation({
    mutationFn: async ({ f, sheet }: { f: File; sheet?: string }) => {
      const formData = new FormData()
      formData.append('file', f)
      if (sheet) formData.append('sheet_name', sheet)
      return api.post('/fund-upload/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data as ParseResult)
    },
    onSuccess: (data) => {
      setParseResult(data)
      setSelectedSheet(data.selected_sheet)
      // Auto-detect date column
      const dateLike = data.columns.find((c) =>
        /date|period|month/i.test(c)
      )
      if (dateLike) setDateCol(dateLike)
      // If Excel with multiple sheets, go to sheet step
      if (data.sheets.length > 1 && step === 'file') {
        setStep('sheet')
      } else {
        setStep('mapping')
      }
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to parse file' })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  // --- Date detection mutation ---
  const detectDatesMutation = useMutation({
    mutationFn: (values: string[]) =>
      api.post('/fund-upload/detect-dates', { values }).then((r) => r.data as DateDetection),
    onSuccess: (data) => {
      setDateDetection(data)
      setDateFormat(data.format)
    },
  })

  // --- Import mutation ---
  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file || !selectedFund) throw new Error('Missing file or fund')
      const formData = new FormData()
      formData.append('file', file)
      formData.append('fund_id', String(selectedFund.fund_id))
      formData.append('date_col', dateCol)
      formData.append('value_col', valueCol)
      formData.append('value_type', valueType)
      if (dateFormat) formData.append('date_format', dateFormat)
      if (selectedSheet) formData.append('sheet_name', selectedSheet)
      formData.append('performance_type_id', String(performanceTypeId))
      formData.append('on_conflict', onConflict)
      return api.post('/fund-upload/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data as ImportResult)
    },
    onSuccess: (data) => {
      setImportResult(data)
      setStep('result')
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Import failed' })
      setTimeout(() => setMessage(null), 8000)
    },
  })

  // --- File drop handlers ---
  const handleFile = (f: File) => {
    const ext = f.name.split('.').pop()?.toLowerCase()
    if (!ext || !['csv', 'xls', 'xlsx'].includes(ext)) {
      setMessage({ type: 'error', text: 'File must be CSV or Excel (.csv, .xls, .xlsx)' })
      setTimeout(() => setMessage(null), 5000)
      return
    }
    setFile(f)
    parseMutation.mutate({ f })
  }

  // --- Dropzone ---
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) handleFile(acceptedFiles[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    multiple: false,
  })

  // --- Auto-detect dates when dateCol changes ---
  useEffect(() => {
    if (dateCol && parseResult) {
      const colIdx = parseResult.columns.indexOf(dateCol)
      if (colIdx >= 0) {
        const values = parseResult.sample_rows.map((row) => row[colIdx]).filter(Boolean)
        detectDatesMutation.mutate(values)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateCol])

  // --- Step indicator ---
  const steps: { key: Step; label: string }[] = [
    { key: 'fund', label: 'Fund' },
    { key: 'file', label: 'File' },
    ...(parseResult?.sheets && parseResult.sheets.length > 1 ? [{ key: 'sheet' as Step, label: 'Sheet' }] : []),
    { key: 'mapping', label: 'Mapping' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'result', label: 'Result' },
  ]

  const currentStepIdx = steps.findIndex((s) => s.key === step)

  const reset = () => {
    setStep('fund')
    setSelectedFund(null)
    setFundQuery('')
    setFile(null)
    setParseResult(null)
    setDateCol('')
    setValueCol('')
    setDateDetection(null)
    setImportResult(null)
    setMessage(null)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Fund Data Upload</h1>
        <p className="text-muted-foreground">
          Upload CSV or Excel files to create or update fund return data
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                i < currentStepIdx
                  ? 'bg-green-100 text-green-700'
                  : i === currentStepIdx
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {i < currentStepIdx ? (
                <CheckCircle className="h-3.5 w-3.5" />
              ) : (
                <span>{i + 1}</span>
              )}
              {s.label}
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className="h-3.5 w-3.5 mx-1 text-muted-foreground" />
            )}
          </div>
        ))}
      </div>

      {/* Message */}
      {message && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ============================================================ */}
      {/* STEP 1: Fund Selection */}
      {/* ============================================================ */}
      {step === 'fund' && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Select Fund</h2>

          {selectedFund ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">{selectedFund.name}</p>
                <p className="text-xs text-muted-foreground">
                  Fund ID: {selectedFund.fund_id}
                  {selectedFund.firm_name && ` | ${selectedFund.firm_name}`}
                </p>
              </div>
              <button
                onClick={() => setSelectedFund(null)}
                className="ml-auto text-sm text-muted-foreground hover:text-foreground"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search funds by name..."
                  className="w-full h-10 pl-9 pr-3 border border-input rounded-md bg-background text-sm"
                  value={fundQuery}
                  onChange={(e) => setFundQuery(e.target.value)}
                />
                {fundSearching && (
                  <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground animate-spin" />
                )}
              </div>
              {fundResults.length > 0 && (
                <div className="border border-border rounded-md max-h-60 overflow-auto">
                  {fundResults.map((f) => (
                    <button
                      key={f.fund_id}
                      onClick={() => {
                        setSelectedFund(f)
                        setFundResults([])
                        setFundQuery('')
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b border-border last:border-b-0"
                    >
                      <span className="font-medium">{f.name}</span>
                      {f.firm_name && (
                        <span className="text-muted-foreground ml-2">({f.firm_name})</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-2">#{f.fund_id}</span>
                    </button>
                  ))}
                </div>
              )}
              {fundQuery.length >= 2 && !fundSearching && fundResults.length === 0 && (
                <p className="text-sm text-muted-foreground">No funds found.</p>
              )}
            </>
          )}

          {/* Create new fund */}
          {!selectedFund && (
            <div>
              <button
                onClick={() => setShowCreateFund(!showCreateFund)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="h-3.5 w-3.5" />
                Create new fund
              </button>

              {showCreateFund && (
                <div className="mt-3 p-4 bg-muted/50 rounded-md space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Fund Name</label>
                    <input
                      type="text"
                      className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                      value={newFundName}
                      onChange={(e) => setNewFundName(e.target.value)}
                      placeholder="Enter fund name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Investment Manager (optional)</label>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                        value={newManagerQuery}
                        onChange={(e) => {
                          setNewManagerQuery(e.target.value)
                          setSelectedManagerId(null)
                        }}
                        placeholder="Search managers..."
                      />
                    </div>
                    {managerResults.length > 0 && !selectedManagerId && (
                      <div className="border border-border rounded-md mt-1 max-h-40 overflow-auto">
                        {managerResults.map((m) => (
                          <button
                            key={m.manager_id}
                            onClick={() => {
                              setSelectedManagerId(m.manager_id)
                              setNewManagerQuery(m.name)
                              setManagerResults([])
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm border-b last:border-b-0"
                          >
                            {m.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {newManagerQuery.length >= 2 && managerResults.length === 0 && !selectedManagerId && (
                      <button
                        onClick={() => createManagerMutation.mutate(newManagerQuery)}
                        disabled={createManagerMutation.isPending}
                        className="mt-1 text-xs text-primary hover:underline"
                      >
                        + Create "{newManagerQuery}" as new manager
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Strategy (optional)</label>
                    <input
                      type="text"
                      className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                      value={newStrategy}
                      onChange={(e) => setNewStrategy(e.target.value)}
                      placeholder="e.g. Long/Short Equity"
                    />
                  </div>
                  <button
                    onClick={() =>
                      createFundMutation.mutate({
                        name: newFundName,
                        manager_id: selectedManagerId,
                        strategy: newStrategy || null,
                      })
                    }
                    disabled={!newFundName || createFundMutation.isPending}
                    className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                  >
                    {createFundMutation.isPending ? 'Creating...' : 'Create Fund'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Next */}
          {selectedFund && (
            <div className="flex justify-end">
              <button
                onClick={() => setStep('file')}
                className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 flex items-center gap-2"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* STEP 2: File Upload */}
      {/* ============================================================ */}
      {step === 'file' && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Upload File</h2>
          <p className="text-sm text-muted-foreground">
            Drop a CSV or Excel file, or click to browse.
          </p>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            {parseMutation.isPending ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                <p className="text-sm text-muted-foreground">Parsing file...</p>
              </div>
            ) : file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="h-8 w-8 text-primary" />
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">Drop file here or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  Supports .csv, .xls, .xlsx
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('fund')}
              className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* STEP 3: Sheet Selection (Excel only) */}
      {/* ============================================================ */}
      {step === 'sheet' && parseResult && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Select Sheet</h2>
          <p className="text-sm text-muted-foreground">
            This Excel file has {parseResult.sheets.length} sheets. Select the one containing fund data.
          </p>

          <div className="space-y-2">
            {parseResult.sheets.map((sheet) => (
              <label
                key={sheet}
                className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer ${
                  selectedSheet === sheet
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <input
                  type="radio"
                  name="sheet"
                  checked={selectedSheet === sheet}
                  onChange={() => setSelectedSheet(sheet)}
                  className="text-primary"
                />
                <span className="text-sm font-medium">{sheet}</span>
              </label>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('file')}
              className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={() => {
                if (file && selectedSheet !== parseResult.selected_sheet) {
                  parseMutation.mutate({ f: file, sheet: selectedSheet || undefined })
                } else {
                  setStep('mapping')
                }
              }}
              className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 flex items-center gap-2"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* STEP 4: Column Mapping */}
      {/* ============================================================ */}
      {step === 'mapping' && parseResult && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Map Columns</h2>
          <p className="text-sm text-muted-foreground">
            {parseResult.row_count.toLocaleString()} rows from <span className="font-medium">{parseResult.filename}</span>
            {parseResult.selected_sheet && (
              <> (sheet: <span className="font-medium">{parseResult.selected_sheet}</span>)</>
            )}
          </p>

          {/* Column mapping selectors */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date Column</label>
              <select
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={dateCol}
                onChange={(e) => setDateCol(e.target.value)}
              >
                <option value="">Select...</option>
                {parseResult.columns.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {dateDetection && dateDetection.confidence > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Format: <span className="font-mono">{dateDetection.format}</span>
                  {' '}({Math.round(dateDetection.confidence * 100)}% confidence)
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Value Column</label>
              <select
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={valueCol}
                onChange={(e) => setValueCol(e.target.value)}
              >
                <option value="">Select...</option>
                {parseResult.columns.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Value Type</label>
              <select
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={valueType}
                onChange={(e) => setValueType(e.target.value)}
              >
                {VALUE_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Data preview */}
          {parseResult.sample_rows.length > 0 && (
            <div className="overflow-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th className="text-muted-foreground">#</th>
                    {parseResult.columns.map((c) => (
                      <th
                        key={c}
                        className={
                          c === dateCol
                            ? 'bg-blue-50 text-blue-700'
                            : c === valueCol
                            ? 'bg-green-50 text-green-700'
                            : ''
                        }
                      >
                        {c}
                        {c === dateCol && <span className="ml-1 text-xs font-normal">(date)</span>}
                        {c === valueCol && <span className="ml-1 text-xs font-normal">(value)</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parseResult.sample_rows.map((row, i) => (
                    <tr key={i}>
                      <td className="text-muted-foreground">{i + 1}</td>
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={
                            parseResult.columns[j] === dateCol
                              ? 'bg-blue-50/50'
                              : parseResult.columns[j] === valueCol
                              ? 'bg-green-50/50'
                              : ''
                          }
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Date detection preview */}
          {dateDetection && dateDetection.parsed_samples.length > 0 && (
            <div className="bg-muted/50 rounded-md p-3">
              <p className="text-xs font-medium mb-1">Parsed date preview:</p>
              <div className="flex flex-wrap gap-2">
                {dateDetection.parsed_samples.map((d, i) => (
                  <span
                    key={i}
                    className={`text-xs px-2 py-0.5 rounded ${
                      d ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {d || 'parse error'}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() =>
                setStep(parseResult.sheets.length > 1 ? 'sheet' : 'file')
              }
              className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={() => setStep('confirm')}
              disabled={!dateCol || !valueCol}
              className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* STEP 5: Confirm & Import */}
      {/* ============================================================ */}
      {step === 'confirm' && parseResult && selectedFund && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Review & Import</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted/50 rounded-md p-4 space-y-2 text-sm">
              <h3 className="font-semibold text-xs uppercase text-muted-foreground">Fund</h3>
              <p>{selectedFund.name} <span className="text-muted-foreground">(#{selectedFund.fund_id})</span></p>
            </div>
            <div className="bg-muted/50 rounded-md p-4 space-y-2 text-sm">
              <h3 className="font-semibold text-xs uppercase text-muted-foreground">File</h3>
              <p>{file?.name}</p>
              <p className="text-xs text-muted-foreground">{parseResult.row_count.toLocaleString()} rows</p>
            </div>
            <div className="bg-muted/50 rounded-md p-4 space-y-2 text-sm">
              <h3 className="font-semibold text-xs uppercase text-muted-foreground">Column Mapping</h3>
              <p>Date: <span className="font-mono">{dateCol}</span></p>
              <p>Value: <span className="font-mono">{valueCol}</span></p>
              <p>Type: {VALUE_TYPES.find((t) => t.id === valueType)?.label}</p>
              {dateDetection?.format && (
                <p>Date format: <span className="font-mono">{dateDetection.format}</span></p>
              )}
            </div>
            <div className="bg-muted/50 rounded-md p-4 space-y-3 text-sm">
              <h3 className="font-semibold text-xs uppercase text-muted-foreground">Options</h3>
              <div>
                <label className="block text-xs font-medium mb-1">Performance Type</label>
                <select
                  className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  value={performanceTypeId}
                  onChange={(e) => setPerformanceTypeId(parseInt(e.target.value))}
                >
                  {PERFORMANCE_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Duplicate Handling</label>
                <select
                  className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  value={onConflict}
                  onChange={(e) => setOnConflict(e.target.value as 'skip' | 'overwrite')}
                >
                  <option value="skip">Skip duplicates</option>
                  <option value="overwrite">Overwrite existing</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('mapping')}
              className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
              className="h-9 px-6 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* STEP 6: Result */}
      {/* ============================================================ */}
      {step === 'result' && importResult && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <h2 className="text-lg font-semibold">Import Complete</h2>
              <p className="text-sm text-muted-foreground">
                {selectedFund?.name}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{importResult.inserted}</p>
              <p className="text-xs text-green-600">Inserted</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{importResult.updated}</p>
              <p className="text-xs text-blue-600">Updated</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{importResult.skipped}</p>
              <p className="text-xs text-amber-600">Skipped</p>
            </div>
          </div>

          {importResult.date_range && (
            <p className="text-sm text-muted-foreground">
              Date range: {importResult.date_range.min} to {importResult.date_range.max}
            </p>
          )}

          {importResult.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
              {importResult.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600">{e}</p>
              ))}
            </div>
          )}

          <button
            onClick={reset}
            className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
          >
            Upload Another File
          </button>
        </div>
      )}
    </div>
  )
}
